import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { LOGIN_SUCCESS } from '../../store/action-types';
import { getMessage } from '../../store/activity-stream/selectors';
import { fetchAlbumPageData } from '../../store/media/albums/actions';
import {
  isLoadingAlbum,
  getAlbum,
  getImage,
  getAlbumLoadingError,
  nextImage,
  prevImage,
} from '../../store/media/albums/selectors';
import Album from '../../components/album';
import Photo from '../../components/album/photo';
import ClubFrame from '../../components/club-frame';
import Club404 from '../../components/errors/club-404';
import PhotoLoading from '../../components/media/photo-loading';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import { withTranslation } from '../../lib/i18n';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';

class AlbumPage extends React.Component {
  componentDidMount() {
    const { loadingError } = this.props;
    if (!loadingError) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const {
      isLoading,
      album,
      club: { galleryEnabled },
    } = this.props;
    if (galleryEnabled && !isLoading && !album) {
      const { loadPageData, clubId, albumId } = this.props;
      loadPageData(clubId, albumId);
    }
  };

  render() {
    const { club, loadingError, routeName, t, frameless } = this.props;

    if (!club.galleryEnabled) {
      return <Club404 club={club} />;
    }

    if (loadingError) {
      const content = (
        <PageDataLoadError
          clubId={club.id}
          accessDeniedTranslationKey="media:album_not_authorised"
          onAuthorise={this.loadIfRequired}
          {...loadingError}
        />
      );
      // When unable to access a private photo we still need the club frame
      if (frameless) {
        return <ClubFrame club={club}>{content}</ClubFrame>;
      }
      return content;
    }

    const { album, image, message, isLoading, nextImg, prevImg } = this.props;
    let title = t('common:loading_title');
    if (routeName === 'photo') {
      if (isLoading) {
        return <PhotoLoading />;
      }

      if (!image) {
        return null;
      }

      ({ title } = image);
      return (
        <Photo
          album={album}
          club={club}
          image={image}
          nextImage={nextImg}
          prevImage={prevImg}
          title={title}
          t={t}
        />
      );
    }

    if (album) {
      ({ title } = album);
    } else if (message) {
      ({ title } = message);
    }

    return <Album album={album} club={club} isLoading={isLoading} title={title} t={t} />;
  }
}

const getRouteNameFromPath = (path) => {
  const clubPrefixRegex = /^\/?clubs\/([a-zA-Z0-9\-_]+)\/(.*)/;
  const result = clubPrefixRegex.exec(path);
  const strippedPath = result ? result[2] : path;

  const photoRegex = /^\/?photos\/([0-9]+)\/([0-9]+)\.html/;
  if (photoRegex.exec(strippedPath)) {
    return 'photo';
  }

  return 'album';
};

AlbumPage.getInitialProps = async ({ asPath, query, isServer, reduxStore, club, req, res }) => {
  const albumId = parseInt(query.id, 10);
  const imageId = query.imageId ? parseInt(query.imageId, 10) : null;
  const routeName = getRouteNameFromPath(asPath);
  const clubId = club.id;
  const initialProps = {
    activeSection: 'photos',
    albumId,
    clubId,
    imageId,
    routeName,
    frameless: routeName === 'photo',
    namespacesRequired: ['common', 'media'],
    pageType: `media.photos.${routeName}`,
  };
  if (!isServer) {
    return initialProps;
  }

  if (!club.galleryEnabled) {
    res.statusCode = 404;
    return initialProps;
  }

  try {
    const { error, accessLevel } = await reduxStore.dispatch(
      fetchAlbumPageData(clubId, albumId, req),
    );
    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }

    if (accessLevel && accessLevel === 'private') {
      res.doNotCache = true;
      if (!error) {
        reduxStore.dispatch({ type: LOGIN_SUCCESS });
      }
    }

    return initialProps;
  } catch (err) {
    return initialProps;
  }
};

AlbumPage.defaultProps = {
  album: null,
  image: null,
  loadingError: null,
  message: null,
  nextImg: null,
  prevImg: null,
};

AlbumPage.propTypes = {
  album: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
  }),
  albumId: PropTypes.number.isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  clubId: PropTypes.number.isRequired,
  image: PropTypes.shape({
    commentCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  isLoading: PropTypes.bool.isRequired,
  loadingError: PropTypes.shape({
    message: PropTypes.string,
    statusCode: PropTypes.number,
  }),
  loadPageData: PropTypes.func.isRequired,
  message: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }),
  nextImg: PropTypes.shape({
    commentCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  prevImg: PropTypes.shape({
    commentCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  routeName: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  frameless: PropTypes.bool.isRequired,
};

const dispatchToProps = {
  loadPageData: fetchAlbumPageData,
};

const mapStateToProps = (state, ownProps) => ({
  album: getAlbum(state, ownProps.albumId),
  image: getImage(state, ownProps.albumId, ownProps.imageId),
  nextImg: nextImage(state, ownProps.albumId, ownProps.imageId),
  isLoading: isLoadingAlbum(state, ownProps.albumId),
  loadingError: getAlbumLoadingError(state, ownProps.albumId),
  message: getMessage(state, `album-${ownProps.albumId}`),
  prevImg: prevImage(state, ownProps.albumId, ownProps.imageId),
});

export default connect(
  mapStateToProps,
  dispatchToProps,
)(withTranslation(['common', 'media'])(AlbumPage));
